/**
 * Notification Service
 * Notification (bildirim) işlemleri için API servisi
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS } from '../config/endpoints';
import {
  // Request types (oluşturulacak)
  NotificationListRequest,
  NotificationUpdatePreferencesRequest,
  NotificationCreateRequest,
  
  // Response types (oluşturulacak)
  NotificationResponse,
  NotificationPreferencesResponse,
  NotificationStatisticsResponse,
  NotificationUnreadCountResponse,
  
  // Common types
  ID,
  ApiResponse,
  PaginatedResponse,
  BaseQueryParams
} from '../types';

/**
 * Notification service interface
 */
export interface INotificationService {
  // Notification Management
  getNotifications(params?: NotificationListRequest): Promise<PaginatedResponse<NotificationResponse>>;
  getNotificationById(id: ID): Promise<ApiResponse<NotificationResponse>>;
  getUnreadNotifications(params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>>;
  getUnreadCount(): Promise<ApiResponse<NotificationUnreadCountResponse>>;
  
  // Notification Actions
  markAsRead(id: ID): Promise<ApiResponse<void>>;
  markAsUnread(id: ID): Promise<ApiResponse<void>>;
  markAllAsRead(): Promise<ApiResponse<void>>;
  deleteNotification(id: ID): Promise<ApiResponse<void>>;
  archiveNotification(id: ID): Promise<ApiResponse<void>>;
  unarchiveNotification(id: ID): Promise<ApiResponse<void>>;
  
  // Bulk Operations
  bulkMarkAsRead(ids: ID[]): Promise<ApiResponse<void>>;
  bulkDelete(ids: ID[]): Promise<ApiResponse<void>>;
  bulkArchive(ids: ID[]): Promise<ApiResponse<void>>;
  
  // Preferences & Settings
  getPreferences(): Promise<ApiResponse<NotificationPreferencesResponse>>;
  updatePreferences(data: NotificationUpdatePreferencesRequest): Promise<ApiResponse<NotificationPreferencesResponse>>;
  getNotificationSettings(): Promise<ApiResponse<any>>;
  updateNotificationSettings(settings: any): Promise<ApiResponse<any>>;
  
  // Statistics & Analytics
  getNotificationStatistics(): Promise<ApiResponse<NotificationStatisticsResponse>>;
  getNotificationTrends(params?: BaseQueryParams): Promise<ApiResponse<any>>;
  
  // Notification Creation (for testing/admin)
  createNotification(data: NotificationCreateRequest): Promise<ApiResponse<NotificationResponse>>;
  
  // Real-time & WebSocket
  subscribeToNotifications(callback: (notification: NotificationResponse) => void): () => void;
  unsubscribeFromNotifications(): void;
  
  // Utility Methods
  searchNotifications(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>>;
  filterNotifications(filters: Record<string, any>): Promise<PaginatedResponse<NotificationResponse>>;
  getNotificationCategories(): Promise<ApiResponse<Array<{ id: string; name: string; description?: string }>>>;
  getNotificationTypes(): Promise<ApiResponse<Array<{ id: string; name: string; description?: string }>>>;
}

/**
 * Notification Service Implementation
 */
class NotificationService implements INotificationService {
  private readonly notificationCallbacks: Set<(notification: NotificationResponse) => void> = new Set();
  private isSubscribed = false;
  
  // ==================== NOTIFICATION MANAGEMENT ====================
  
  async getNotifications(params?: NotificationListRequest): Promise<PaginatedResponse<NotificationResponse>> {
    return apiClient.getList<NotificationResponse>(ENDPOINTS.NOTIFICATIONS.LIST, params, {
      cacheKey: `notifications_${JSON.stringify(params || {})}`,
      cacheTTL: 60000 // 1 dakika cache
    });
  }
  
  async getNotificationById(id: ID): Promise<ApiResponse<NotificationResponse>> {
    return apiClient.customGet<NotificationResponse>(
      ENDPOINTS.NOTIFICATIONS.MARK_AS_READ(id).replace('/read', ''),
      {
        cacheKey: `notification_${id}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async getUnreadNotifications(params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>> {
    return apiClient.getList<NotificationResponse>(ENDPOINTS.NOTIFICATIONS.UNREAD, params, {
      cacheKey: `notifications_unread_${JSON.stringify(params || {})}`,
      cacheTTL: 30000 // 30 saniye cache (sık değişir)
    });
  }
  
  async getUnreadCount(): Promise<ApiResponse<NotificationUnreadCountResponse>> {
    return apiClient.customGet<NotificationUnreadCountResponse>(
      ENDPOINTS.NOTIFICATIONS.UNREAD,
      {
        params: { countOnly: true },
        cacheKey: `notifications_unread_count_${Date.now()}`,
        cacheTTL: 10000 // 10 saniye cache (real-time)
      }
    );
  }
  
  // ==================== NOTIFICATION ACTIONS ====================
  
  async markAsRead(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`notification_${id}`);
    apiClient.clearCache(/notifications_unread/);
    
    return apiClient.customPost<void>(
      ENDPOINTS.NOTIFICATIONS.MARK_AS_READ(id),
      {}
    );
  }
  
  async markAsUnread(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`notification_${id}`);
    apiClient.clearCache(/notifications_unread/);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/${id}/unread`,
      {}
    );
  }
  
  async markAllAsRead(): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<void>(
      ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ,
      {}
    );
  }
  
  async deleteNotification(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`notification_${id}`);
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customDelete<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/${id}`
    );
  }
  
  async archiveNotification(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`notification_${id}`);
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/${id}/archive`,
      {}
    );
  }
  
  async unarchiveNotification(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`notification_${id}`);
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/${id}/unarchive`,
      {}
    );
  }
  
  // ==================== BULK OPERATIONS ====================
  
  async bulkMarkAsRead(ids: ID[]): Promise<ApiResponse<void>> {
    // Cache'i temizle
    for (const id of ids) {
      apiClient.clearCache(`notification_${id}`);
    }
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/bulk/read`,
      { ids }
    );
  }
  
  async bulkDelete(ids: ID[]): Promise<ApiResponse<void>> {
    // Cache'i temizle
    for (const id of ids) {
      apiClient.clearCache(`notification_${id}`);
    }
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/bulk/delete`,
      { ids }
    );
  }
  
  async bulkArchive(ids: ID[]): Promise<ApiResponse<void>> {
    // Cache'i temizle
    for (const id of ids) {
      apiClient.clearCache(`notification_${id}`);
    }
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/bulk/archive`,
      { ids }
    );
  }
  
  // ==================== PREFERENCES & SETTINGS ====================
  
  async getPreferences(): Promise<ApiResponse<NotificationPreferencesResponse>> {
    return apiClient.customGet<NotificationPreferencesResponse>(
      ENDPOINTS.NOTIFICATIONS.PREFERENCES,
      {
        cacheKey: 'notification_preferences',
        cacheTTL: 3600000 // 1 saat cache
      }
    );
  }
  
  async updatePreferences(data: NotificationUpdatePreferencesRequest): Promise<ApiResponse<NotificationPreferencesResponse>> {
    // Cache'i temizle
    apiClient.clearCache('notification_preferences');
    
    return apiClient.customPut<NotificationPreferencesResponse>(
      ENDPOINTS.NOTIFICATIONS.PREFERENCES,
      data
    );
  }
  
  async getNotificationSettings(): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      ENDPOINTS.NOTIFICATIONS.SETTINGS,
      {
        cacheKey: 'notification_settings',
        cacheTTL: 3600000 // 1 saat cache
      }
    );
  }
  
  async updateNotificationSettings(settings: any): Promise<ApiResponse<any>> {
    // Cache'i temizle
    apiClient.clearCache('notification_settings');
    
    return apiClient.customPut<any>(
      ENDPOINTS.NOTIFICATIONS.SETTINGS,
      settings
    );
  }
  
  // ==================== STATISTICS & ANALYTICS ====================
  
  async getNotificationStatistics(): Promise<ApiResponse<NotificationStatisticsResponse>> {
    return apiClient.customGet<NotificationStatisticsResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/statistics`,
      {
        cacheKey: 'notification_statistics',
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async getNotificationTrends(params?: BaseQueryParams): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/trends`,
      {
        params,
        cacheKey: `notification_trends_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  // ==================== NOTIFICATION CREATION ====================
  
  async createNotification(data: NotificationCreateRequest): Promise<ApiResponse<NotificationResponse>> {
    // Cache'i temizle
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customPost<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/create`,
      data
    );
  }
  
  // ==================== REAL-TIME & WEBSOCKET ====================
  
  subscribeToNotifications(callback: (notification: NotificationResponse) => void): () => void {
    this.notificationCallbacks.add(callback);
    
    // İlk abonelikte WebSocket bağlantısını başlat
    if (!this.isSubscribed) {
      this.startWebSocketConnection();
      this.isSubscribed = true;
    }
    
    // Cleanup function
    return () => {
      this.notificationCallbacks.delete(callback);
      
      // Tüm callback'ler kaldırıldıysa WebSocket'i kapat
      if (this.notificationCallbacks.size === 0) {
        this.stopWebSocketConnection();
        this.isSubscribed = false;
      }
    };
  }
  
  unsubscribeFromNotifications(): void {
    this.notificationCallbacks.clear();
    this.stopWebSocketConnection();
    this.isSubscribed = false;
  }
  
  private startWebSocketConnection(): void {
    // WebSocket bağlantısını başlat
    // Bu kısım projenizin WebSocket yapısına göre özelleştirilmeli
    console.log('WebSocket notification subscription started');
    
    // Örnek EventSource/WebSocket implementasyonu:
    try {
      // WebSocket bağlantısı
      const wsUrl = `${globalThis.location.origin.replace('http', 'ws')}/ws/notifications`;
      const socket = new WebSocket(wsUrl);
      
      socket.onmessage  = (event) => {
        const data = JSON.parse(event.data) as NotificationResponse;
        this.handleIncomingNotification(data);
      }
      // veya Server-Sent Events
      // const eventSource = new EventSource('/api/notifications/stream');
      
      // Burada gerçek implementasyon proje yapınıza bağlı
    } catch (error) {
      console.error('Failed to start WebSocket connection:', error);
    }
  }
  
  private stopWebSocketConnection(): void {
    // WebSocket bağlantısını durdur
    console.log('WebSocket notification subscription stopped');
    
    // WebSocket veya EventSource'u kapat
  }
  
  private handleIncomingNotification(notification: NotificationResponse): void {
    // Tüm callback'leri çağır
    for (const callback of this.notificationCallbacks) {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification callback:', error);
      }
    }
    
    // Cache'i güncelle
    apiClient.clearCache(/notifications_unread/);
  }
  
  // ==================== UTILITY METHODS ====================
  
  async searchNotifications(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>> {
    const searchParams = {
      ...params,
      search: query
    };
    
    return apiClient.getList<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/search`,
      searchParams,
      {
        cacheKey: `notifications_search_${query}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async filterNotifications(filters: Record<string, any>): Promise<PaginatedResponse<NotificationResponse>> {
    return apiClient.getList<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/filter`,
      filters,
      {
        cacheKey: `notifications_filter_${JSON.stringify(filters)}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async getNotificationCategories(): Promise<ApiResponse<Array<{ id: string; name: string; description?: string }>>> {
    return apiClient.customGet<Array<{ id: string; name: string; description?: string }>>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/categories`,
      {
        cacheKey: 'notification_categories',
        cacheTTL: 3600000 // 1 saat cache
      }
    );
  }
  
  async getNotificationTypes(): Promise<ApiResponse<Array<{ id: string; name: string; description?: string }>>> {
    return apiClient.customGet<Array<{ id: string; name: string; description?: string }>>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/types`,
      {
        cacheKey: 'notification_types',
        cacheTTL: 3600000 // 1 saat cache
      }
    );
  }
  
  // ==================== ADDITIONAL METHODS ====================
  
  /**
   * Get notifications by category
   */
  async getNotificationsByCategory(category: string, params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>> {
    return apiClient.getList<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/category/${category}`,
      params,
      {
        cacheKey: `notifications_category_${category}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get notifications by type
   */
  async getNotificationsByType(type: string, params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>> {
    return apiClient.getList<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/type/${type}`,
      params,
      {
        cacheKey: `notifications_type_${type}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get archived notifications
   */
  async getArchivedNotifications(params?: BaseQueryParams): Promise<PaginatedResponse<NotificationResponse>> {
    return apiClient.getList<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/archived`,
      params,
      {
        cacheKey: `notifications_archived_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Clear all notifications
   */
  async clearAllNotifications(): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customDelete<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/clear`
    );
  }
  
  /**
   * Clear old notifications (older than X days)
   */
  async clearOldNotifications(days: number): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(/notifications_/);
    
    return apiClient.customDelete<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/clear-old`,
      {
        params: { days }
      }
    );
  }
  
  /**
   * Test notification (for admin/testing)
   */
  async sendTestNotification(type: string): Promise<ApiResponse<NotificationResponse>> {
    return apiClient.customPost<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/test`,
      { type }
    );
  }
  
  /**
   * Get notification delivery status
   */
  async getDeliveryStatus(notificationId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/${notificationId}/delivery-status`
    );
  }
  
  /**
   * Retry failed notification
   */
  async retryNotification(notificationId: ID): Promise<ApiResponse<void>> {
    return apiClient.customPost<void>(
      `${ENDPOINTS.NOTIFICATIONS.BASE}/${notificationId}/retry`
    );
  }
}

// Singleton instance
export const notificationService = new NotificationService();

// Default export
export default notificationService;

export { NotificationService };